import { useRef, useCallback } from 'react';
import { Play, Pause } from 'lucide-react';
import dayjs from 'dayjs';

const SPEED_CYCLE = [1, 2, 4, 8];

function fmt(secs) {
  const abs = Math.abs(secs || 0);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Compact playback row for the floating Trip Player: play/pause, speed cycle,
 * scrubber (pauses while dragging, resumes on release), and elapsed/total.
 */
export default function ReplayPlayerControls({
  positions, progress, playing, speed, currentPosition, onToggle, onSeek, onSpeedChange,
}) {
  const wasPlaying = useRef(false);

  const cycleSpeed = useCallback(() => {
    const i = SPEED_CYCLE.indexOf(speed);
    onSpeedChange(SPEED_CYCLE[(i + 1) % SPEED_CYCLE.length]);
  }, [speed, onSpeedChange]);

  const onDown = useCallback(() => { wasPlaying.current = playing; if (playing) onToggle(); }, [playing, onToggle]);
  const onInput = useCallback((e) => onSeek(Number(e.target.value) / 1000), [onSeek]);
  const onUp = useCallback(() => { if (wasPlaying.current) onToggle(); }, [onToggle]);

  const totalSecs = positions && positions.length >= 2
    ? dayjs(positions[positions.length - 1].fixTime).diff(dayjs(positions[0].fixTime), 'second') : 0;
  const elapsedSecs = currentPosition && positions && positions.length
    ? dayjs(currentPosition.fixTime).diff(dayjs(positions[0].fixTime), 'second') : 0;

  return (
    <div className="flex items-center gap-2.5 px-3.5 pb-2.5">
      <button
        type="button"
        onClick={onToggle}
        className="w-9 h-9 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center flex-shrink-0 shadow-md transition-colors"
      >
        {playing ? <Pause size={16} /> : <Play size={16} />}
      </button>
      <button
        type="button"
        onClick={cycleSpeed}
        className="text-xs font-semibold text-blue-600 bg-blue-600/10 px-2 py-1 rounded-lg flex-shrink-0"
      >
        {speed}×
      </button>
      <input
        type="range" min="0" max="1000" step="1"
        value={Math.round(progress * 1000)}
        onMouseDown={onDown} onTouchStart={onDown}
        onInput={onInput} onChange={() => {}}
        onMouseUp={onUp} onTouchEnd={onUp}
        className="flex-1 min-w-0 h-1 rounded-full appearance-none cursor-pointer
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white
          [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-600 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white"
        style={{ background: `linear-gradient(to right, #2563eb ${progress * 100}%, #e2e8f0 ${progress * 100}%)` }}
      />
      <span className="text-[11px] font-mono text-slate-500 tabular-nums flex-shrink-0 whitespace-nowrap">
        <bdi>{fmt(elapsedSecs)} / {fmt(totalSecs)}</bdi>
      </span>
    </div>
  );
}
